import React, { memo, useState, useCallback, useEffect, useRef } from 'react';
import Text from '@wconcept/wconcept-ui-renewal/components/atoms/Text';
import Img from '@wconcept/wconcept-ui-renewal/components/atoms/Img';
import Button from '@wconcept/wconcept-ui-renewal/components/atoms/Button';
import { classNameBind } from '@wconcept/wconcept-common/utils/commonUtils';
import { getResizeImageUrl } from '@wconcept/wconcept-common/utils/domainUtil';
import VeVideo from '@wconcept/wconcept-ui-renewal/components/atoms/VeVideo';
import { IPlayAuthTokenConfig } from '@byteplus/veplayer';
import PlayIcon from '@wconcept/wconcept-ui-renewal/components/molecules/_icon/PlayIcon';

import * as S from './style';

interface IProps {
  videoSrc?: string;
  videoPoster?: string;
  vid?: string;
  token?: IPlayAuthTokenConfig;
  priority?: boolean;
  isActive?: boolean;
  itemRef?: React.Ref<HTMLDivElement>;
  userName?: string;
  userImgSrc?: string;
  desc?: string;
  relatedPrd?: React.ReactNode;
  rs?: string;
  isDesktop?: boolean;
  isLastInGroup?: boolean;
  isPaused?: boolean;
  onClickProfile?: () => void;
  onClick?: () => void;
  getNewToken?: () => Promise<IPlayAuthTokenConfig>;
  onVideoEnd?: () => void;
}

const ShortformItem2 = ({
  videoSrc,
  videoPoster,
  vid,
  token,
  priority = false,
  isActive = false,
  itemRef,
  userName,
  userImgSrc,
  desc,
  relatedPrd,
  rs,
  isDesktop,
  isLastInGroup = false,
  isPaused = false,
  onClickProfile,
  onClick,
  getNewToken,
  onVideoEnd,
}: IProps) => {
  const [videoEl, setVideoEl] = useState<any>(null);
  const [isVideoPlay, setIsVideoPlay] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const cName = classNameBind([
    'shortform-item item',
    isVideoPlay ? 'isLoaded' : '',
  ]);

  // 아이템 활성화/비활성화에 따른 비디오 재생 및 타이머 관리
  useEffect(() => {
    const videoElement = videoEl?.player;

    if (isActive) {
      // 활성화된 경우: 비디오 재생 시작
      if (videoElement) {
        if (isPaused) {
          // isPaused가 true면 비디오 일시정지 (currentTime은 유지)
          videoElement.pause();
          setIsVideoPlay(false);
        } else {
          // isPaused가 false면 비디오 재생 (멈춘 부분부터 재생)
          const playPromise = videoElement.play();

          if (playPromise !== undefined) {
            playPromise
              .then(() => {
                // 모바일에서만 추가 안정성 체크
                if (!isDesktop && videoElement.paused) {
                  videoElement.play();
                }
                setIsVideoPlay(true);
              })
              .catch(() => {
                // 재생 실패 시 처리
              });
          }
        }
      }

      // 데스크톱에서만 5초 타이머 설정 (isPaused가 false일 때만)
      if (isDesktop && !isPaused) {
        const shouldRepeatPlay = isLastInGroup; // 데스크톱의 그룹 내 마지막 아이템

        if (shouldRepeatPlay) {
          // 5초마다 무한 반복 재생
          const repeatPlay = () => {
            if (videoElement && !isPaused) {
              // 비디오가 있고 isPaused가 false일 때만 처음부터 다시 재생
              videoElement.currentTime = 0;
              videoElement.play();
            }
            // 5초 후 다시 이 함수를 실행하여 무한 반복
            timerRef.current = setTimeout(repeatPlay, 5000);
          };
          timerRef.current = setTimeout(repeatPlay, 5000);
        } else {
          // 일반 아이템들: 5초 후 다음 아이템으로 넘어감
          timerRef.current = setTimeout(() => {
            if (!isPaused) {
              onVideoEnd?.(); // 부모 컴포넌트의 handleVideoEnd 함수 호출
            }
          }, 5000);
        }
      } else if (isPaused) {
        // isPaused가 true면 타이머 정리
        if (timerRef.current) {
          clearTimeout(timerRef.current);
          timerRef.current = null;
        }
      }
    } else {
      // 비활성화된 경우: 모든 재생 중단 및 리셋
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }

      if (videoElement) {
        videoElement.pause(); // 비디오 일시정지
        videoElement.currentTime = 0; // 재생 위치를 처음으로 되돌림
        setIsVideoPlay(false); // 재생 상태 리셋
      }
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [isActive, videoEl, onVideoEnd, isDesktop, isLastInGroup, isPaused]);

  // ITEM RENDERER : getRelatedPrdRender(연관상품)
  const getRelatedPrdRender = useCallback(() => {
    if (relatedPrd) {
      return <div className="area-relatedPrd">{relatedPrd}</div>;
    }
    return null;
  }, [relatedPrd]);

  return (
    <S.ShortformItem2 className={cName} ref={itemRef}>
      <Button onClick={onClick} className="video-wrap">
        {isActive && (
          <VeVideo
            src={videoSrc}
            muted
            priority={priority}
            loop
            vid={vid}
            getNewToken={getNewToken}
            token={token}
            handleComplete={el => {
              if (el) setVideoEl(el);
            }}
          />
        )}
        <Img
          className="poster-img"
          priority={priority}
          src={getResizeImageUrl(videoPoster, rs)}
        />
        <PlayIcon size={isDesktop ? 'lg' : 'md'} />
      </Button>
      <Button className="user-info" onClick={onClickProfile}>
        <Img
          className="user-img"
          src={getResizeImageUrl(userImgSrc, rs)}
          alt={userName}
        />
        <Text className="user-name" color="#fff" weight={500} ellipsis={1}>
          {userName}
        </Text>
      </Button>
      <div className="shortform-info">
        <Text className="desc" color="#fff" ellipsis={2} block>
          {desc}
        </Text>
        {getRelatedPrdRender()}
      </div>
    </S.ShortformItem2>
  );
};

export default memo(ShortformItem2);
